import { useState } from 'react';

import { ApiError } from '../../api/client';
import { importApi, type Campaign, type ImportAnalysis, type ImportMapping, type ImportReport } from '../../api/campaigns';
import { Field, Notice } from '../../components/ui';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';

type Step = 'file' | 'mapping' | 'duplicates' | 'result';

/**
 * CSV-Import in vier ruhigen Schritten: Datei, Spalten, doppelte Einträge,
 * Ergebnis (04_contacts_csv.md).
 *
 * Vor dem letzten Schritt wird nichts gespeichert.
 */
export function ImportWizard({ campaign, onDone }: { campaign: Campaign; onDone: () => void }): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [step, setStep] = useState<Step>('file');
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);
  const [mapping, setMapping] = useState<ImportMapping | null>(null);
  const [strategy, setStrategy] = useState<'skip' | 'overwrite'>('skip');
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<void>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  const chooseFile = (file: File | undefined): void => {
    if (!file) {
      return;
    }
    void run(async () => {
      const result = await importApi.upload(campaign.id, file);
      setAnalysis(result);
      setMapping(result.suggestedMapping);
      setStep('mapping');
    });
  };

  const updateMapping = (changes: Partial<ImportMapping>): void => {
    if (!analysis || !mapping) {
      return;
    }
    const next = { ...mapping, ...changes };
    setMapping(next);
    void run(async () => {
      const result = await importApi.analyze(campaign.id, analysis.importId, next);
      setAnalysis(result.analysis);
    });
  };

  const start = (): void => {
    if (!analysis || !mapping) {
      return;
    }
    void run(async () => {
      const result = await importApi.commit(campaign.id, analysis.importId, mapping, strategy);
      setReport(result.report);
      setStep('result');
    });
  };

  const spaltenAuswahl = (
    label: TranslationKey,
    value: string | null | undefined,
    onChange: (value: string | null) => void,
  ): JSX.Element => (
    <Field label={t(label)}>
      <select value={value ?? ''} onChange={(event) => onChange(event.target.value || null)}>
        <option value="">{t('import.mapping.none')}</option>
        {(analysis?.columns ?? []).map((column) => (
          <option key={column} value={column}>
            {column}
          </option>
        ))}
      </select>
    </Field>
  );

  return (
    <section className="card">
      <h2>{t('import.title')}</h2>

      <ol className="steps">
        {(['file', 'mapping', 'duplicates', 'result'] as Step[]).map((entry) => (
          <li key={entry} className={entry === step ? 'step step-active' : 'step'}>
            {t(`import.step.${entry}` as TranslationKey)}
          </li>
        ))}
      </ol>

      {error ? <Notice tone="error">{error}</Notice> : null}

      {step === 'file' ? (
        <div className="form">
          <Field label={t('import.chooseFile')} hint={t('import.fileHint')}>
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              onChange={(event) => chooseFile(event.target.files?.[0])}
            />
          </Field>
        </div>
      ) : null}

      {analysis && (step === 'mapping' || step === 'duplicates') ? (
        <>
          <dl className="facts">
            <dt>{t('import.rows')}</dt>
            <dd>{analysis.totalRows}</dd>
            <dt>{t('import.validPhones')}</dt>
            <dd>{analysis.validPhones}</dd>
            <dt>{t('import.invalidPhones')}</dt>
            <dd>{analysis.invalidPhones}</dd>
            <dt>{t('import.duplicates')}</dt>
            <dd>{analysis.duplicates}</dd>
            {analysis.rowsWithPublicId > 0 ? (
              <>
                <dt>{t('import.withId')}</dt>
                <dd>{analysis.rowsWithPublicId}</dd>
              </>
            ) : null}
          </dl>

          {analysis.sampleRows.length > 0 ? (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    {analysis.columns.map((column) => (
                      <th key={column}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analysis.sampleRows.map((row, index) => (
                    <tr key={index}>
                      {analysis.columns.map((column) => (
                        <td key={column}>{row[column]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {step === 'mapping' && mapping ? (
        <>
          <div className="form form-inline">
            <Field label={t('import.mapping.phone')}>
              <select
                value={mapping.phoneColumn}
                onChange={(event) => updateMapping({ phoneColumn: event.target.value })}
              >
                {(analysis?.columns ?? []).map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </Field>
            {spaltenAuswahl('import.mapping.firstName', mapping.firstNameColumn, (value) =>
              updateMapping({ firstNameColumn: value }),
            )}
            {spaltenAuswahl('import.mapping.lastName', mapping.lastNameColumn, (value) =>
              updateMapping({ lastNameColumn: value }),
            )}
          </div>
          <p className="field-hint">{t('import.mapping.hint')}</p>

          <div className="row-actions">
            <button type="button" className="primary" disabled={busy} onClick={() => setStep('duplicates')}>
              {t('contacts.next')}
            </button>
            <button type="button" className="link-button" onClick={onDone}>
              {t('action.cancel')}
            </button>
          </div>
        </>
      ) : null}

      {step === 'duplicates' ? (
        <>
          <p className="lead">{t('import.duplicates.question')}</p>
          <label className="checkbox">
            <input
              type="radio"
              name="strategy"
              checked={strategy === 'skip'}
              onChange={() => setStrategy('skip')}
            />
            {t('import.duplicates.skip')}
          </label>
          <label className="checkbox">
            <input
              type="radio"
              name="strategy"
              checked={strategy === 'overwrite'}
              onChange={() => setStrategy('overwrite')}
            />
            {t('import.duplicates.overwrite')}
          </label>
          <p className="field-hint">{t('import.duplicates.overwriteHint')}</p>

          <div className="row-actions">
            <button type="button" className="primary" disabled={busy} onClick={start}>
              {busy ? t('import.running') : t('import.start')}
            </button>
            <button type="button" className="link-button" onClick={() => setStep('mapping')}>
              {t('contacts.previous')}
            </button>
          </div>
        </>
      ) : null}

      {step === 'result' && report ? (
        <>
          <dl className="facts">
            <dt>{t('import.result.created')}</dt>
            <dd>{report.created}</dd>
            <dt>{t('import.result.updated')}</dt>
            <dd>{report.updated}</dd>
            <dt>{t('import.result.skippedDuplicates')}</dt>
            <dd>{report.skippedDuplicates}</dd>
            <dt>{t('import.result.skippedInvalid')}</dt>
            <dd>{report.skippedInvalid}</dd>
          </dl>

          {report.createdFields.length > 0 ? (
            <p className="field-hint">
              {t('import.result.createdFields')}: {report.createdFields.join(', ')}
            </p>
          ) : null}

          {report.errors.length > 0 ? (
            <>
              <h3>{t('import.result.errors')}</h3>
              <ul className="list">
                {report.errors.map((entry) => (
                  <li key={entry.line} className="list-row">
                    {t(`import.error.${entry.reason}` as TranslationKey, { line: entry.line })}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          <button type="button" className="primary" onClick={onDone}>
            {t('import.done')}
          </button>
        </>
      ) : null}
    </section>
  );
}
